import {
    AfterViewInit, ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, first, map, skip, takeUntil } from 'rxjs/operators';

import { EnvironmentCmsUrls } from '@core/domain/types/environment-config.types';
import { getOrientation } from '@core/infrastructure/state/core.selectors';
import { ConfigService } from '@core/services/config/config.service';
import { ContentItemTree } from '@features/cms-render/domain/models/contentItemTree';
import {
    CommonStylesRenderDirective,
} from '@features/cms-render/ui/directives/common-styles-render/common-styles-render.directive';
import { SkeletonStyles } from '@features/cms-render/ui/render-pieces/md-skeleton/md-skeleton.models';
import { PieceVideoUtils } from '@features/cms-render/ui/render-pieces/piece-video/piece-video.utils';
import { AssetsSharedService } from '@features/cms-render/ui/services/assets-shared/assets-shared.service';
import { GenerateSrcsetService } from '@features/cms-render/ui/services/assets-shared/generate-srcset.service';
import { HlsService } from '@features/cms-render/ui/services/hls/hls.service';
import { getLastSectionSelector } from '@shared/menu-categories/store/menu-categories.selectors';

@Component({
    selector: 'piece-video',
    templateUrl: './piece-video.component.html',
    styleUrls: ['./piece-video.component.scss'],
    providers: [HlsService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PieceVideoComponent extends CommonStylesRenderDirective implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('videoElement') private readonly videoElement?: ElementRef<HTMLVideoElement>;

    @HostBinding('class') public aligmentClasses: string | undefined;

    @Input() public element: ContentItemTree;

    @HostBinding('class.marquee')
    public isMarqueeTextAnimationEnabled: boolean;

    @HostBinding('style.--marquee-duration-desktop')
    public marqueeTextDurationDesktop: string | undefined;

    @HostBinding('style.--marquee-duration-mobile')
    public marqueeTextDurationMobile: string | undefined;

    public autoplay: number;

    public loop: number;

    public autoPlay: number;

    public muted: number;

    public controls: number;

    public poster: string;

    public audioControls: number;

    public audioIconPosition: string;

    public hasToPlaceIconRelative: boolean;

    private readonly onDestroy$ = new Subject();

    // eslint-disable-next-line max-len
    public videoData$: Observable<{ videoSrc: string; poster: string; skeleton: SkeletonStyles; skeletonObj: any }> = this.store.select(getOrientation)
        .pipe(
            map(orientation => orientation ?? 'portrait'),
            first(),
            map((orientation) => {
                const general = this.element.settings.general;
                const skeleton = PieceVideoUtils.getSkeleton(general, orientation);

                const skeletonObj = orientation === 'portrait'
                    ? { width: general.portraitPosterImage.width, height: general.portraitPosterImage.height }
                    : { width: general.landscapePosterImage.width, height: general.landscapePosterImage.height };

                const assetsUrl = `${this.configService.getConfigUrl<EnvironmentCmsUrls>('cmsUrls').content}/assets/`;
                const akamaiImageVideoManagerConfig = {
                    enabled: false,
                };

                let video: string;
                let image: string;

                if (orientation === 'portrait') {
                    video = general.videoPortrait
                        ? general.videoPortrait
                        : general.video;

                    image = general.portraitPosterImage.image;
                } else {
                    video = general.videoLandscape
                        ? general.videoLandscape
                        : general.video;

                    image = general.landscapePosterImage.image;
                }

                const poster = this.assetsSharedService.getAssetUrl(
                    image,
                    assetsUrl,
                    akamaiImageVideoManagerConfig
                );

                const videoSrc = this.assetsSharedService.getAssetUrl(
                    video,
                    assetsUrl,
                    akamaiImageVideoManagerConfig
                );

                const imageLandscape = general.landscapePosterImage.image;
                const imagePortrait = general.portraitPosterImage.image;

                const posterSrcSet = this.generateSrcsetService.generateSrcset(imageLandscape, imagePortrait, assetsUrl);

                return {
                    skeleton,
                    skeletonObj: skeletonObj,
                    poster,
                    videoSrc,
                    posterSrcSet,
                };
            })
        );

    public showSkeleton = true;

    public showOnlyPoster = true;

    constructor(
        private readonly configService: ConfigService,
        private readonly assetsSharedService: AssetsSharedService,
        private readonly generateSrcsetService: GenerateSrcsetService,
        private readonly cdr: ChangeDetectorRef,
        private readonly store: Store,
    ) {
        super();
    }

    public ngOnInit(): void {
        super.setDefaultHostStyles();
        this.marqueeDurations();

        if (!!this.element.settings) {
            this.aligmentClasses = this.applyAlignmentClasses();
            this.autoPlay = this.element.settings.general.autoPlay ? 1 : 0;
            this.muted = this.autoPlay === 1 ? 1 : 0;
            this.loop = this.element.settings.general.loop ? 1 : 0;
            this.controls = this.element.settings.general.showControls ? 1 : 0;
            this.audioControls = this.element.settings.general.showMediaAudio ? 1 : 0;
            this.audioIconPosition = this.element.settings.general.audioIconPosition ?? 'bottom-left';
            this.hasToPlaceIconRelative = this.element.settings.general.audioIconPosition === 'top-text'
                || this.element.settings.general.audioIconPosition === 'bottom-text';
        }

        this.store.select(getLastSectionSelector)
            .pipe(
                filter(section => !!section),
                skip(1),
                takeUntil(this.onDestroy$)
            )
            .subscribe((_) => {
                // soluciona el bug que si vas a otra home se sigue escuchando el video anteorior
                this.muted = 1;
                this.cdr.detectChanges();
            });
    }

    public ngAfterViewInit() {
        if (this.element.name.includes('no-lazy')) this.deferLoad();
    }

    public applyAlignmentClasses(): string {
        const classes: string[] = [];
        if (this.element.settings.styles.textPositionVertical) {
            classes.push(`vertical-${this.element.settings.styles.textPositionVertical}`);
        }
        return classes.join(' ');
    }

    public async playVideoWithoutControls(video: HTMLVideoElement) {
        if (!this.controls && this.element.settings.general.clickToPlayPause) {
            if (video.paused) {
                await video.play();
            } else {
                video.pause();
            }
        }
    }

    public mute(event?: Event): void {
        if (!!event) {
            event.stopPropagation();
            event.preventDefault();
        }
        this.muted = this.muted === 0 ? 1 : 0;
    }

    public deferLoad(): void {
        this.showSkeleton = false;
    }

    public marqueeDurations(): void {
        this.isMarqueeTextAnimationEnabled = !!this.element.settings?.general?.marquee?.marqueeStatus;

        if (this.isMarqueeTextAnimationEnabled) {
            const marqueeDurationDesktopValue = this.element.settings?.general?.marquee?.marqueeDurationDesktop;
            const marqueeDurationMobileValue = this.element.settings?.general?.marquee?.marqueeDurationMobile;

            this.marqueeTextDurationDesktop = marqueeDurationDesktopValue ? `${marqueeDurationDesktopValue}s` : '20s';
            this.marqueeTextDurationMobile = marqueeDurationMobileValue ? `${marqueeDurationMobileValue}s` : '5s';
        } else {
            this.marqueeTextDurationDesktop = undefined;
            this.marqueeTextDurationMobile = undefined;
        }
    }

    public ngOnDestroy(): void {
        this.onDestroy$.next(undefined);
        this.onDestroy$.complete();
    }
}
